import { DateOnly } from '@if/core/date'
import { devToolsHistoryKeys } from '@src/api/dev-tools-history-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const TERMINATE_ETL_BATCH_ROUTE = 'dev-tools/terminate-etl-batch-orchestrator'
const TERMINATE_LOAN_VALIDATION_ROUTE = 'dev-tools/terminate-loan-validation-orchestrator'
const IMPORT_ALL_IFX_LOANS_ROUTE = 'dev-tools/import-all-ifx-loans'
const IMPORT_LOANS_BY_IDS_ROUTE = 'dev-tools/import-loans-by-ids'
const IMPORT_ALL_IFX_BANKJOB = 'dev-tools/import-all-ifx-bankjob'
const REMOVE_LOANS_BY_PROGRAM_ROUTE = 'dev-tools/remove-loans-by-program'
const MOCK_FTF_FILE_ROUTE = 'dev-tools/mock-ftf-file'

export interface RemoveLoansByProgramRequest {
  // One or more financing programs to delete. The "All" UI option sends every program.
  financingProgramIds: string[]
}

const terminateEtlBatchOrchestrator = async (): Promise<void> => {
  try {
    await apiClient.post(TERMINATE_ETL_BATCH_ROUTE)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const terminateLoanValidationOrchestrator = async (): Promise<void> => {
  try {
    await apiClient.post(TERMINATE_LOAN_VALIDATION_ROUTE)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const importAllIfxLoans = async (): Promise<void> => {
  try {
    await apiClient.post(IMPORT_ALL_IFX_LOANS_ROUTE)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const importLoansByIds = async (loanIds: string[]): Promise<void> => {
  try {
    await apiClient.post(IMPORT_LOANS_BY_IDS_ROUTE, { loanIds })
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const importAllIfxBankJob = async (): Promise<void> => {
  try {
    await apiClient.post(IMPORT_ALL_IFX_BANKJOB)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const removeLoansByProgram = async (request: RemoveLoansByProgramRequest): Promise<void> => {
  try {
    await apiClient.post(REMOVE_LOANS_BY_PROGRAM_ROUTE, request)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const useInvalidateCommandHistory = () => {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: devToolsHistoryKeys.all })
  }
}

export const useTerminateEtlBatchOrchestrator = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: terminateEtlBatchOrchestrator,
    onSettled: invalidateHistory,
  })
}

export const useTerminateLoanValidationOrchestrator = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: terminateLoanValidationOrchestrator,
    onSettled: invalidateHistory,
  })
}

export const useImportAllIfxLoans = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: importAllIfxLoans,
    onSettled: invalidateHistory,
  })
}

export const useImportLoansByIds = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: importLoansByIds,
    onSettled: invalidateHistory,
  })
}

export const useImportAllIfxBankJob = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: importAllIfxBankJob,
    onSettled: invalidateHistory,
  })
}

export const useRemoveLoansByProgram = () => {
  const invalidateHistory = useInvalidateCommandHistory()
  return useMutation({
    mutationFn: removeLoansByProgram,
    onSettled: invalidateHistory,
  })
}

export interface GenerateMockFtfFileRequest {
  fileDate: DateOnly
  returnCount: number
}

export interface MockFtfFile {
  fileName: string
  content: string
  // Payments actually returned: lower than requested when not enough completed PAD payments qualify.
  returnCount: number
}

const generateMockFtfFile = async (request: GenerateMockFtfFileRequest): Promise<MockFtfFile> => {
  try {
    const { data } = await apiClient.post<MockFtfFile>(MOCK_FTF_FILE_ROUTE, request)
    return data
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

// No history to invalidate: generating a mock FTF file is not recorded.
export const useGenerateMockFtfFile = () => useMutation({ mutationFn: generateMockFtfFile })
